import { useCallback, useRef, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { resolveLogoUrl } from '../utils';
export function useMessengerPreview({ bulletinBoardEnabled, title, description, startConversation, findArticles, backgroundColor, logo, } = {}, bulletins, avatars, knowledgeState) {
    const [previewLoaded, setPreviewLoaded] = useState(false);
    const previewIframeRef = useRef(null);
    const sendFormValuesOverToPreview = useCallback((payload) => {
        const timeoutId = setTimeout(() => {
            var _a, _b;
            (_b = (_a = previewIframeRef.current) === null || _a === void 0 ? void 0 : _a.contentWindow) === null || _b === void 0 ? void 0 : _b.postMessage(payload, '*');
        });
        return () => clearTimeout(timeoutId);
    }, []);
    const updateBgColor = useCallback((color) => {
        if (color !== undefined) {
            sendFormValuesOverToPreview({ type: 'setBgColor', payload: color });
        }
    }, [sendFormValuesOverToPreview]);
    const updateLocalizedTitle = useCallback((title) => {
        if (title !== undefined) {
            sendFormValuesOverToPreview({
                type: 'setLocalizedTitle',
                payload: title,
            });
        }
    }, [sendFormValuesOverToPreview]);
    const updateLocalizedDescription = useCallback((description) => {
        if (description !== undefined) {
            sendFormValuesOverToPreview({
                type: 'setLocalizedDescription',
                payload: description,
            });
        }
    }, [sendFormValuesOverToPreview]);
    const updateLocalizedStartConversation = useCallback((startConversation) => {
        if (startConversation !== undefined) {
            sendFormValuesOverToPreview({
                type: 'setLocalizedStartConversation',
                payload: startConversation,
            });
        }
    }, [sendFormValuesOverToPreview]);
    const updateLocalizedFindArticles = useCallback((findArticles) => {
        if (findArticles !== undefined) {
            sendFormValuesOverToPreview({
                type: 'setLocalizedFindArticles',
                payload: findArticles,
            });
        }
    }, [sendFormValuesOverToPreview]);
    const updateBulletinActive = useCallback((bulletinBoardEnabled) => {
        if (bulletinBoardEnabled !== undefined) {
            sendFormValuesOverToPreview({
                type: 'setBulletinActive',
                payload: bulletinBoardEnabled,
            });
        }
    }, [sendFormValuesOverToPreview]);
    const updateBulletinList = useCallback((bulletins) => {
        if (bulletins !== undefined) {
            sendFormValuesOverToPreview({
                type: 'setBulletinList',
                payload: bulletins.filter(({ active }) => active),
            });
        }
    }, [sendFormValuesOverToPreview]);
    const setPreviewLanguage = useCallback((language) => {
        var _a;
        sendFormValuesOverToPreview({
            type: 'setLanguage',
            payload: language,
        });
        (_a = previewIframeRef.current) === null || _a === void 0 ? void 0 : _a.setAttribute('dir', isRtlLanguage(language) ? 'rtl' : 'ltr');
    }, [sendFormValuesOverToPreview]);
    const updateAvatars = useCallback((avatars) => {
        sendFormValuesOverToPreview({
            type: 'setAvatars',
            payload: avatars,
        });
    }, [sendFormValuesOverToPreview]);
    const updateLogo = useCallback((logo) => {
        sendFormValuesOverToPreview({
            type: 'setLogo',
            payload: resolveLogoUrl(logo),
        });
    }, [sendFormValuesOverToPreview]);
    const updateElevioIntegration = useCallback((payload) => {
        sendFormValuesOverToPreview({
            type: 'setElevioIntegration',
            payload,
        });
    }, [sendFormValuesOverToPreview]);
    const updateElevioIntegrationKnowledgeFirst = useCallback((knowledgeState) => {
        const { elevioIntegration, selectedLanguage } = knowledgeState;
        if (elevioIntegration.type === 'connected') {
            const { enabled, displayType, translations } = elevioIntegration;
            const { findArticles, searchBarPlaceholder } = translations;
            if (enabled) {
                setPreviewLanguage(selectedLanguage);
                updateElevioIntegration({
                    displayType,
                    searchBarPlaceholder: searchBarPlaceholder.translatedMap,
                });
                updateLocalizedFindArticles(findArticles.translatedMap);
            }
            else {
                updateElevioIntegration();
            }
        }
        else {
            updateElevioIntegration();
        }
    }, [setPreviewLanguage, updateElevioIntegration, updateLocalizedFindArticles]);
    useUpdateEffect(() => updateBgColor(backgroundColor), [backgroundColor]);
    useUpdateEffect(() => updateLocalizedTitle(title), [title]);
    useUpdateEffect(() => updateLocalizedDescription(description), [description]);
    useUpdateEffect(() => updateLocalizedStartConversation(startConversation), [startConversation]);
    useUpdateEffect(() => updateLocalizedFindArticles(findArticles), [findArticles]);
    useUpdateEffect(() => updateBulletinActive(bulletinBoardEnabled), [bulletinBoardEnabled]);
    useUpdateEffect(() => updateBulletinList(bulletins), [bulletins]);
    useUpdateEffect(() => updateLogo(logo), [logo]);
    useUpdateEffect(() => updateElevioIntegrationKnowledgeFirst(knowledgeState), [knowledgeState]);
    const handlePreviewLoaded = useCallback(() => {
        // first set the initial values so that the widget loads with all the correct customizations,
        // before it is presented to the user
        updateBgColor(backgroundColor);
        updateLocalizedTitle(title);
        updateLocalizedDescription(description);
        updateLocalizedStartConversation(startConversation);
        updateLocalizedFindArticles(findArticles);
        updateBulletinActive(bulletinBoardEnabled);
        updateBulletinList(bulletins);
        updateAvatars(avatars);
        updateLogo(logo);
        updateElevioIntegrationKnowledgeFirst(knowledgeState);
        // Then end the loading
        setTimeout(() => {
            setPreviewLoaded(true);
        });
    }, [
        backgroundColor,
        title,
        description,
        startConversation,
        findArticles,
        bulletinBoardEnabled,
        bulletins,
        avatars,
        logo,
        updateBgColor,
        updateLocalizedTitle,
        updateLocalizedDescription,
        updateLocalizedStartConversation,
        updateLocalizedFindArticles,
        updateBulletinActive,
        updateBulletinList,
        updateAvatars,
        updateLogo,
        knowledgeState,
        updateElevioIntegrationKnowledgeFirst,
    ]);
    return {
        loaded: previewLoaded,
        ref: previewIframeRef,
        onLoad: handlePreviewLoaded,
        setPreviewLanguage,
    };
}
function isRtlLanguage(languageCode) {
    const rtlLanguages = ['ar'];
    return rtlLanguages.includes(languageCode);
}
